import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { Form } from "@itandi/itandi-bb-ui/Form";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import { useStatuses } from "~/hooks/useStatuses";
import type { DealType } from "~/models/Deal";
import type { TodoTemplate, TodoTemplateEdit } from "~/models/TodoTemplate";
import {
	buildInitialTodoTemplateEdit,
	validationSchema
} from "~/models/TodoTemplate";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/TodoTemplate";
import { onInvalidScroll } from "~/utils/FormUtil";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	dealType: DealType;
	handleClose: () => void;
	subtypeId: number | null;
	todoTemplate?: TodoTemplate | null;
}>;

export function TodoTemplateModal({
	dealType,
	subtypeId,
	handleClose,
	todoTemplate = null
}: Props) {
	const formId = useId();
	const { data: statuses } = useStatuses().index();
	const {
		formState: { errors },
		handleSubmit,
		register
	} = useForm<TodoTemplateEdit>({
		defaultValues:
			todoTemplate ?? buildInitialTodoTemplateEdit(dealType, subtypeId),
		mode: "onBlur",
		resolver: yupResolver(validationSchema)
	});

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/todo_templates"),
		async (_key, { arg }: { arg: TodoTemplateEdit }) => {
			return todoTemplate === null ? create(arg) : update(todoTemplate.id, arg);
		}
	);

	const onSubmit = async (submitValue: TodoTemplateEdit): Promise<void> => {
		const response = await trigger(submitValue);
		if (isSuccess(response)) {
			handleClose();
			toast.success(
				todoTemplate === null ? "作成しました。" : "更新しました。"
			);
		} else {
			toast.error("エラーが発生しました。");
		}
	};

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader
				title={
					todoTemplate ? "TODOテンプレートの編集" : "TODOテンプレートの作成"
				}
			/>
			<ModalBody>
				<Form id={formId} onSubmit={handleSubmit(onSubmit, onInvalidScroll)}>
					<FormGroup variant="horizontal">
						<FormControl
							chip="必須"
							isInvalid={!!errors.statusId}
							label="作成ステータス"
						>
							<InputSelect {...register("statusId", { valueAsNumber: true })}>
								{statuses?.map((status) => (
									<option key={status.id} value={status.id}>
										{status.uniqueDisplayName}
									</option>
								))}
							</InputSelect>
							<FormControlText error={errors.statusId?.message} />
						</FormControl>
						<Divider />
						<FormControl
							chip="必須"
							isInvalid={!!errors.title}
							label="TODOタイトル"
						>
							<Input placeholder="例）書類確認" {...register("title")} />
							<FormControlText error={errors.title?.message} />
						</FormControl>
						<Divider />
						<FormControl isInvalid={!!errors.memo} label="メモ">
							<InputText placeholder="例）書類確認手順" {...register("memo")} />
							<FormControlText error={errors.memo?.message} />
						</FormControl>
					</FormGroup>
				</Form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isMutating}
						onClick={(): void => {
							handleClose();
						}}
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						{todoTemplate ? "更新" : "作成"}
					</Button>
				}
			/>
		</Modalv2>
	);
}
